/**
 * The receiving side: a replica of every entity the server has put in this
 * peer's view, kept up to date packet by packet. Updated entities are new
 * objects (the old one is left as it was), so a history of them can be
 * kept for interpolation.
 */
import { Reader } from "./bytes";
import type { Entity, EntityOf, Id, Protocol, Types } from "./schema";
import { FORMAT } from "./server";

export interface Changed<T> {
  created: T[];
  updated: T[];
  removed: T[];
}

/** What one packet did to each entity type. */
export type Changes<M extends Types> = { [K in keyof M]: Changed<EntityOf<M[K]>> };

interface Replica {
  byId: Map<Id, Record<string, unknown>>;
  byHandle: (Record<string, unknown> | undefined)[];
}

export class SnapshotClient<M extends Types> {
  private readonly entities: Entity[];
  private readonly replicas: Replica[];
  private readonly mask: number[] = [];

  constructor(readonly protocol: Protocol<M>) {
    this.entities = protocol.names.map((n) => protocol.types[n]!);
    this.replicas = this.entities.map(() => ({ byId: new Map(), byHandle: [] }));
  }

  /** Forget everything (the server's peer was reset, or this is a new connection). */
  reset(): void {
    for (const r of this.replicas) {
      r.byId.clear();
      r.byHandle.length = 0;
    }
  }

  /** Every entity of `type` in view, by id, in the order they came into view. */
  get<K extends keyof M & string>(type: K): ReadonlyMap<Id, EntityOf<M[K]>> {
    const i = this.protocol.names.indexOf(type);
    if (i < 0) throw new Error(`netsync: no entity type "${type}"`);
    return this.replicas[i]!.byId as unknown as ReadonlyMap<Id, EntityOf<M[K]>>;
  }

  /** Apply one packet, in the order they were sent. Throws if it doesn't fit what came before (reconnect then). */
  apply(bytes: Uint8Array): Changes<M> {
    const r = new Reader(bytes);
    const format = r.u8();
    if (format !== FORMAT) throw new Error(`netsync: packet format ${format}, expected ${FORMAT}`);
    const changes = {} as Record<string, Changed<unknown>>;
    for (let t = 0; t < this.entities.length; t++) {
      const ent = this.entities[t]!;
      const rep = this.replicas[t]!;
      const out: Changed<unknown> = { created: [], updated: [], removed: [] };
      changes[this.protocol.names[t]!] = out;

      let n = r.uvar();
      for (let k = 0; k < n; k++) {
        const h = r.uvar();
        const e = rep.byHandle[h];
        if (!e) throw new Error(`netsync: removal of unknown ${ent.idKey} handle ${h}`);
        rep.byHandle[h] = undefined;
        rep.byId.delete(e[ent.idKey] as Id);
        out.removed.push(e);
      }
      n = r.uvar();
      for (let k = 0; k < n; k++) {
        const h = r.uvar();
        const id = ent.idField.read(r);
        const e: Record<string, unknown> = { [ent.idKey]: id };
        for (let i = 0; i < ent.fields.length; i++) {
          const v = ent.fields[i]!.read(r);
          if (v !== undefined) e[ent.names[i]!] = v;
        }
        rep.byHandle[h] = e;
        rep.byId.set(id, e);
        out.created.push(e);
      }
      n = r.uvar();
      for (let k = 0; k < n; k++) {
        const h = r.uvar();
        const old = rep.byHandle[h];
        if (!old) throw new Error(`netsync: update to unknown ${ent.idKey} handle ${h}`);
        const groups = readMask(r, this.mask);
        const e = { ...old };
        for (let i = 0; i < ent.fields.length; i++) {
          const g = (i / 7) | 0;
          if (g >= groups || !(this.mask[g]! & (1 << i % 7))) continue;
          const v = ent.fields[i]!.read(r);
          if (v === undefined) delete e[ent.names[i]!];
          else e[ent.names[i]!] = v;
        }
        rep.byHandle[h] = e;
        rep.byId.set(e[ent.idKey] as Id, e);
        out.updated.push(e);
      }
    }
    return changes as Changes<M>;
  }
}

/** Read a change mask (see writeMask) into `into`; returns how many groups of seven it had. */
function readMask(r: Reader, into: number[]): number {
  let g = 0;
  for (;;) {
    const b = r.u8();
    into[g++] = b & 0x7f;
    if (!(b & 0x80)) return g;
  }
}
